import { Suspense, lazy } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import WaitlistForm from "@/components/WaitlistForm";
import ContactForm from "@/components/ContactForm";
import Reveal from "@/components/Reveal";
import { useProgressRef } from "@/three/useProgressRef";

import wideHero from "@/assets/widehero.jpg";
import closeUpHero from "@/assets/closeuphero.jpg";
import caseImg from "@/assets/case.jpg";
import cameraImg from "@/assets/camera.jpg";

const VisionScene = lazy(() => import("@/three/VisionScene"));

function ScrollSection({ id, height = "h-[300vh]", children, testId, className = "" }) {
  return (
    <section id={id} data-testid={testId} className={`relative ${height} ${className}`}>
      {children}
    </section>
  );
}

function StatusStrip() {
  return (
    <div
      className="inline-flex items-center gap-3 border border-white/15 bg-black/40 px-4 py-2 backdrop-blur-xl"
      data-testid="hero-status-strip"
    >
      <span className="relative inline-flex h-2 w-2">
        <span className="pulse-dot absolute inline-flex h-full w-full rounded-full bg-[#ff5500]" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-[#ff5500]" />
      </span>
      <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/70">
        ARVEX · CAPTURE LIVE
      </span>
    </div>
  );
}

const EASE = [0.22, 1, 0.36, 1];

export default function Landing() {
  // Vision: start progress when the section enters the viewport bottom
  const vision = useProgressRef(["start end", "end start"]);

  const { scrollYProgress } = useScroll();
  const heroBgY = useTransform(scrollYProgress, [0, 0.18], [0, 120]);
  const heroBgScale = useTransform(scrollYProgress, [0, 0.18], [1.05, 1.18]);

  return (
    <div className="relative bg-[#050505] text-white" id="top">
      <Nav />

      {/* ==================== HERO ==================== */}
      <section
        id="hero"
        data-testid="hero-section"
        className="relative flex min-h-screen items-center overflow-hidden"
      >
        <motion.div
          style={{ y: heroBgY, scale: heroBgScale }}
          className="absolute inset-0 z-0"
        >
          <motion.img
            src={wideHero}
            alt="Ski racer captured mid-run on an alpine course"
            className="h-full w-full object-cover"
            initial={{ opacity: 0, scale: 1.12 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.6, ease: EASE }}
          />
        </motion.div>

        <div className="absolute inset-0 z-[1] bg-gradient-to-r from-[#050505] via-[#050505]/70 to-transparent" />
        <div className="absolute inset-0 z-[1] bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/40" />

        <div className="relative z-10 mx-auto grid w-full max-w-[1400px] grid-cols-1 items-center gap-12 px-6 pt-28 pb-20 md:px-10 lg:grid-cols-12 lg:pt-24">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: EASE }}
            >
              <StatusStrip />
            </motion.div>

            <motion.div
              className="label-mono mt-6"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.08, ease: EASE }}
            >
              ARVEX · Ski Race Capture System
            </motion.div>

            <h1
              data-testid="hero-headline"
              className="font-display mt-5 text-balance text-4xl font-semibold leading-[1.04] tracking-tight sm:text-5xl md:text-6xl lg:text-7xl"
            >
              {["Capture every run.", "Analyze every turn.", "Improve every session."].map(
                (line, i) => (
                  <motion.span
                    key={line}
                    className={
                      i === 0
                        ? "block"
                        : i === 1
                        ? "block text-white/55"
                        : "block bg-gradient-to-r from-[#ff5500] via-[#ff8a3d] to-[#00e5ff] bg-clip-text text-transparent"
                    }
                    initial={{ opacity: 0, y: 28 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.18 + i * 0.12, ease: EASE }}
                  >
                    {line}
                  </motion.span>
                )
              )}
            </h1>

            <motion.p
              className="mt-7 max-w-xl text-base text-white/65 md:text-lg"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6, ease: EASE }}
            >
              The autonomous capture &amp; timing system for ski race training. Films every
              run, measures every gate, delivers every clip — directly to the athlete&apos;s
              phone.
            </motion.p>

            <motion.div
              className="mt-9 flex flex-wrap items-center gap-3"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.72, ease: EASE }}
            >
              <a
                href="#waitlist"
                data-testid="hero-cta-primary"
                className="btn-primary rounded-full px-6 py-3 font-mono text-xs uppercase tracking-[0.22em]"
              >
                Request Early Access
              </a>
              <a
                href="#device"
                data-testid="hero-cta-secondary"
                className="btn-secondary rounded-full px-6 py-3 font-mono text-xs uppercase tracking-[0.22em]"
              >
                See the System →
              </a>
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              className="relative ml-auto w-full max-w-md"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5, ease: EASE }}
            >
              <div className="glass relative overflow-hidden rounded-2xl p-2">
                <div className="overflow-hidden rounded-xl">
                  <img
                    src={closeUpHero}
                    alt="Close-up of the Arvex capture unit on the slope"
                    className="aspect-[4/3] w-full object-cover"
                  />
                </div>
                <div className="pointer-events-none absolute inset-2 rounded-xl">
                  <div className="absolute left-3 top-3 flex items-center gap-2">
                    <span className="relative inline-flex h-2 w-2">
                      <span className="pulse-dot absolute inline-flex h-full w-full rounded-full bg-[#ff2a00]" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-[#ff2a00]" />
                    </span>
                    <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/80">
                      REC
                    </span>
                  </div>
                  <div className="absolute bottom-3 right-3 font-mono text-[9px] uppercase tracking-[0.3em] text-white/70">
                    4K · 120FPS
                  </div>
                  <span className="absolute left-3 bottom-3 h-3 w-3 border-b border-l border-white/40" />
                  <span className="absolute right-3 top-3 h-3 w-3 border-r border-t border-white/40" />
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">
                <span>Autonomous unit</span>
                <span>Live feed</span>
              </div>
            </motion.div>
          </div>
        </div>

        <motion.div
          className="pointer-events-none absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.8 }}
        >
          <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/40">
            Scroll
          </div>
          <div className="mx-auto mt-2 h-10 w-px bg-gradient-to-b from-white/40 to-transparent" />
        </motion.div>
      </section>

      {/* ==================== STATS MARQUEE ==================== */}
      <section className="relative z-10 overflow-hidden border-y border-white/10 bg-black py-6">
        <div className="marquee-track flex w-max items-center gap-16 whitespace-nowrap">
          {Array.from({ length: 2 }).map((_, k) => (
            <div className="flex items-center gap-16" key={k}>
              {[
                "AUTONOMOUS CAPTURE", "/ /", "1/1000s TIMING", "/ /", "OFFLINE READY", "/ /",
                "INSTANT TO PHONE", "/ /", "BUILT FOR -25°C", "/ /", "CLUB GRADE", "/ /",
              ].map((s, i) => (
                <span
                  key={i}
                  className={`font-mono text-xs uppercase tracking-[0.3em] ${
                    s.startsWith("/") ? "text-[#ff5500]" : "text-white/80"
                  }`}
                >
                  {s}
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* ==================== 01 · THE PROBLEM — Session Dashboard ==================== */}
      <section
        id="problem"
        data-testid="problem-section"
        className="relative z-10 overflow-hidden bg-[#050505] py-16 md:py-24"
      >
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute right-1/4 top-1/4 h-[400px] w-[400px] rounded-full bg-[#ff5500]/[0.05] blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="grid items-start gap-12 lg:grid-cols-2 lg:gap-16">
            {/* Text */}
            <div>
              <Reveal>
                <div className="label-mono mb-6">01 · The Problem</div>
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="font-display max-w-lg text-balance text-4xl font-semibold leading-tight tracking-tight md:text-5xl">
                  The best run of the day. <br />
                  <span className="text-white/50">And nobody filmed it.</span>
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-5 max-w-lg text-base text-white/60 md:text-lg">
                  Coaches can&apos;t be everywhere. Athletes finish a perfect run — and have nothing to review.
                  Timing data lives on a stopwatch, video lives on someone&apos;s phone (if they remembered to hit record).
                  Sessions end with scattered notes and zero accountability.
                </p>
              </Reveal>
              <div className="mt-8 space-y-4">
                <Reveal delay={0.12}>
                  <Bullet label="Missing footage" text="Most training runs go completely uncaptured. No video, no review, no progress." />
                </Reveal>
                <Reveal delay={0.16}>
                  <Bullet label="Lost feedback" text="Without frame-by-frame replay, technical mistakes repeat session after session." />
                </Reveal>
                <Reveal delay={0.2}>
                  <Bullet label="Manual chaos" text="Phones, USB sticks, stopwatches, WhatsApp groups. Training shouldn't be admin." />
                </Reveal>
              </div>
            </div>

            {/* Dashboard mockup */}
            <Reveal direction="right" delay={0.1} duration={0.9}>
              <SessionDashboard />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ==================== 02 · DEVICE ==================== */}
      <section
        id="device"
        data-testid="device-section"
        className="relative z-10 overflow-hidden border-t border-white/10 bg-[#050505] py-16 md:py-24"
      >
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute left-1/2 top-1/3 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-[#00e5ff]/[0.06] blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
          <Reveal>
            <div className="label-mono mb-6">02 · The Arvex Device</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display max-w-lg text-balance text-4xl font-semibold leading-tight tracking-tight md:text-5xl">
              One unit. <br /> Four jobs.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 max-w-2xl text-base text-white/60 md:text-lg">
              A single weatherproof unit replaces the entire sideline crew.
              It sees the slope, times every gate, records every run in 4K,
              and uploads the clip to the athlete&apos;s phone — all without a single person touching it.
              Built to survive -25°C, rain, and snow dumps. Powered for a full training day.
              Just plant it, turn it on, and ski.
            </p>
          </Reveal>

          <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {[
              { no: "01", name: "Camera", desc: "4K wide-angle sensor with automatic racer tracking. Captures the full line from gate to gate without manual panning.", color: "#00e5ff" },
              { no: "02", name: "Capture", desc: "On-device edge recording with buffer loop. Never misses a start — footage rolls before the gate breaks.", color: "#ffffff" },
              { no: "03", name: "Timing", desc: "Sub-millisecond infrared beam gates. Triggers the camera, marks splits, and logs everything to the session timeline.", color: "#ff5500" },
              { no: "04", name: "Upload", desc: "LTE and Wi-Fi push. Clips are on the athlete's phone within seconds of crossing the finish line.", color: "#00e5ff" },
            ].map((d, i) => (
              <Reveal key={d.no} delay={0.08 + i * 0.06}>
                <DeviceLabel {...d} />
              </Reveal>
            ))}
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-2">
            <Reveal delay={0.05}>
              <ImageFrame
                src={caseImg}
                alt="The sealed Arvex weatherproof housing"
                caption="Weatherproof housing"
                sub="Sealed against snow, ice and -25°C"
              />
            </Reveal>
            <Reveal delay={0.12}>
              <ImageFrame
                src={cameraImg}
                alt="The Arvex auto-tracking optics module"
                caption="Auto-tracking optics"
                sub="4K wide-angle that follows the racer"
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ==================== 03 · THE RUN ==================== */}
      <section
        id="run"
        data-testid="run-section"
        className="relative z-10 overflow-hidden border-t border-white/10 bg-black py-16 md:py-24"
      >
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute right-0 top-10 h-[420px] w-[420px] rounded-full bg-[#ff5500]/[0.07] blur-3xl" />
          <div className="absolute left-0 bottom-10 h-[420px] w-[420px] rounded-full bg-[#00e5ff]/[0.06] blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
          <Reveal>
            <div className="label-mono mb-4">03 · The Run</div>
          </Reveal>
          <Reveal delay={0.06}>
            <h2 className="font-display max-w-3xl text-balance text-4xl font-semibold leading-tight tracking-tight md:text-6xl">
              Gate triggers.
              <br />
              <span className="text-[#ff5500]">Camera fires.</span>{" "}
              <span className="text-white/40">·</span>{" "}
              <span className="text-[#00e5ff]">Clip lands on the phone.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-2xl text-base text-white/60 md:text-lg">
              The moment a racer breaks the start beam, Arvex takes over. The camera fires,
              the clock starts, and the entire run is captured in 4K without a coach lifting a finger.
              When the athlete crosses the finish, the clip is packaged with timing data
              and pushed straight to their phone. No manual upload. No USB. No waiting.
              Every single run — documented, timed, and delivered in seconds.
            </p>
          </Reveal>

          <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-4">
            {[
              { n: "01", t: "Start gate breaks", desc: "Infrared beam detects the racer — clock and recording begin simultaneously." },
              { n: "02", t: "Capture begins", desc: "4K footage buffered on-device with pre-roll so the full approach is captured." },
              { n: "03", t: "Finish stops the clock", desc: "Second beam registers the finish split and marks the clip endpoint." },
              { n: "04", t: "Auto-upload to app", desc: "Clip + timing data pushed to the athlete's phone within seconds." },
            ].map((s, i) => (
              <Reveal key={s.n} delay={0.05 + i * 0.06}>
                <StepDetailed n={s.n} t={s.t} desc={s.desc} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== 04 · VISION (3D) ==================== */}
      <ScrollSection id="vision" height="h-screen" testId="vision-section">
        <div ref={vision.sectionRef} className="absolute inset-0">
          <div className="sticky top-0 h-screen w-full overflow-hidden">
            <Suspense fallback={<SceneFallback />}>
              <VisionScene progressRef={vision.progressRef} />
            </Suspense>
            <div className="pointer-events-none absolute inset-0 z-10 mx-auto flex max-w-[1400px] flex-col justify-between px-6 py-24 md:px-16">
              <div>
                <div className="label-mono mb-4">04 · Future Vision</div>
                <h2 className="font-display max-w-3xl text-balance text-4xl font-semibold leading-tight tracking-tight md:text-6xl">
                  Beyond capture.
                  <br />
                  <span className="text-white/50">Toward understanding.</span>
                </h2>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <VisionCard
                  badge="Line Analysis"
                  text="Overlay the ideal racing line against every athlete's actual path. See where meters are gained or lost."
                  color="#ff5500"
                />
                <VisionCard
                  badge="Velocity Curve"
                  text="Per-gate speed, exit-velocity, and acceleration — visualized turn by turn across the full course."
                  color="#ffffff"
                />
                <VisionCard
                  badge="AI Commentary"
                  text="Automated technical feedback after every single run. Body position, line choice, timing — analyzed in seconds."
                  color="#00e5ff"
                />
              </div>
            </div>
          </div>
        </div>
      </ScrollSection>

      {/* ==================== CTA / WAITLIST / CONTACT ==================== */}
      <section
        id="waitlist"
        data-testid="cta-section"
        className="relative z-10 -mt-10 bg-gradient-to-b from-[#050505] to-black px-6 pt-10 pb-16 md:px-10 md:pt-14 md:pb-24"
      >
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute left-1/2 top-1/2 h-[480px] w-[480px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#ff5500]/10 blur-3xl" />
          <div className="absolute right-10 top-20 h-[300px] w-[300px] rounded-full bg-[#00e5ff]/10 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-[1400px]">
          <div className="mx-auto max-w-3xl text-center">
            <Reveal>
              <div className="label-mono">Launching Winter Season</div>
            </Reveal>
            <Reveal delay={0.06}>
              <h2 className="font-display mt-4 text-balance text-5xl font-semibold leading-[0.95] tracking-tighter md:text-7xl">
                Ready for <span className="text-[#ff5500]">first tracks</span>?
              </h2>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mx-auto mt-6 max-w-xl text-white/60">
                Limited pilot units shipping to selected clubs and athletes. Join the
                waitlist or talk to us about equipping your team.
              </p>
            </Reveal>
          </div>

          <div id="contact" className="mt-16 grid gap-6 md:grid-cols-2">
            <WaitlistForm />
            <ContactForm />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

/* ===================== Helpers ===================== */

function SceneFallback() {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-[#050505]">
      <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/40">
        Loading scene…
      </div>
    </div>
  );
}

/* Session dashboard mockup — a realistic training session run log */
function SessionDashboard() {
  // Chronological run log: same athlete appears multiple times as they do runs.
  // delta = compared to their own previous run (not a leaderboard).
  const runs = [
    { id: 1, time: "14:02", athlete: "L. Meier",   result: "53.12", delta: null,    video: true },
    { id: 2, time: "14:04", athlete: "T. Brunner", result: "54.38", delta: null,    video: true },
    { id: 3, time: "14:07", athlete: "M. Hofer",   result: "55.01", delta: null,    video: true },
    { id: 4, time: "14:11", athlete: "L. Meier",   result: "52.67", delta: -0.45,  video: true },
    { id: 5, time: "14:14", athlete: "T. Brunner", result: "53.89", delta: -0.49,  video: true },
    { id: 6, time: "14:18", athlete: "L. Meier",   result: "52.21", delta: -0.46,  video: true },
    { id: 7, time: "14:21", athlete: "M. Hofer",   result: "54.44", delta: -0.57,  video: true },
    { id: 8, time: "14:25", athlete: "T. Brunner", result: "53.52", delta: -0.37,  video: "processing" },
    { id: 9, time: "14:28", athlete: "L. Meier",   result: "—",     delta: null,    video: "recording" },
  ];

  return (
    <div className="glass rounded-2xl p-5 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-white/10 pb-4">
        <div>
          <div className="font-display text-lg font-medium tracking-tight">GS Training</div>
          <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
            Kitzbühel · Streifalm · 22 Jan
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="relative inline-flex h-2 w-2">
            <span className="pulse-dot absolute inline-flex h-full w-full rounded-full bg-[#00e5ff]" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#00e5ff]" />
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#00e5ff]">Live</span>
        </div>
      </div>

      {/* Column headers */}
      <div className="mt-3 flex items-center justify-between px-3 py-1.5">
        <div className="flex items-center gap-3">
          <span className="w-10 font-mono text-[9px] uppercase tracking-[0.2em] text-white/30">Time</span>
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/30">Athlete</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="w-10 text-right font-mono text-[9px] uppercase tracking-[0.2em] text-white/30">vs prev</span>
          <span className="w-12 text-right font-mono text-[9px] uppercase tracking-[0.2em] text-white/30">Result</span>
          <span className="w-5 font-mono text-[9px] uppercase tracking-[0.2em] text-white/30"></span>
        </div>
      </div>

      {/* Run list */}
      <div className="space-y-0.5">
        {runs.map((r) => (
          <div
            key={r.id}
            className="flex items-center justify-between rounded-lg px-3 py-2 transition-colors hover:bg-white/[0.04]"
          >
            <div className="flex items-center gap-3">
              <span className="w-10 font-mono text-[10px] text-white/40">{r.time}</span>
              <span className="text-sm font-medium">{r.athlete}</span>
            </div>
            <div className="flex items-center gap-4">
              {/* Delta vs own previous run */}
              <span className={`w-10 text-right font-mono text-xs ${
                r.delta === null
                  ? "text-white/20"
                  : r.delta < 0
                  ? "text-[#00e5ff]"
                  : "text-[#ff5500]"
              }`}>
                {r.delta === null ? "—" : `${r.delta > 0 ? "+" : ""}${r.delta.toFixed(2)}`}
              </span>
              {/* Run time */}
              <span className="w-12 text-right font-mono text-sm font-medium">
                {r.result}
              </span>
              {/* Video indicator */}
              <span className="w-5 flex justify-center">
                {r.video === true ? (
                  <VideoIcon className="text-green-400" />
                ) : r.video === "processing" ? (
                  <VideoIcon className="text-yellow-400 animate-pulse" />
                ) : r.video === "recording" ? (
                  <RecIcon />
                ) : null}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Session summary */}
      <div className="mt-4 grid grid-cols-3 gap-3 border-t border-white/10 pt-4">
        <div className="text-center">
          <div className="font-display text-xl font-semibold tracking-tight">9</div>
          <div className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-white/45">Runs</div>
        </div>
        <div className="text-center">
          <div className="font-display text-xl font-semibold tracking-tight text-[#00e5ff]">52.21</div>
          <div className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-white/45">Session best</div>
        </div>
        <div className="text-center">
          <div className="font-display text-xl font-semibold tracking-tight text-green-400">8</div>
          <div className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-white/45">Clips ready</div>
        </div>
      </div>
    </div>
  );
}

/* Small video-ready icon */
function VideoIcon({ className = "" }) {
  return (
    <svg className={`h-3.5 w-3.5 ${className}`} viewBox="0 0 16 16" fill="none">
      <rect x="1" y="3" width="10" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
      <path d="M11 6.5L15 4.5V11.5L11 9.5" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}

/* Recording indicator */
function RecIcon() {
  return (
    <span className="relative inline-flex h-2.5 w-2.5">
      <span className="pulse-dot absolute inline-flex h-full w-full rounded-full bg-[#ff2a00]" />
      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#ff2a00]" />
    </span>
  );
}

function ImageFrame({ src, alt, caption, sub }) {
  return (
    <figure className="glass group relative overflow-hidden rounded-2xl p-2">
      <div className="relative overflow-hidden rounded-xl aspect-[16/9]">
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <span className="absolute left-3 bottom-3 h-3 w-3 border-b border-l border-white/30" />
        <span className="absolute right-3 top-3 h-3 w-3 border-r border-t border-white/30" />
        {caption && (
          <figcaption className="absolute inset-x-3 bottom-3">
            <div className="font-display text-lg font-medium tracking-tight text-white">
              {caption}
            </div>
            {sub && (
              <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.22em] text-white/55">
                {sub}
              </div>
            )}
          </figcaption>
        )}
      </div>
    </figure>
  );
}

function Bullet({ label, text }) {
  return (
    <div className="border-l border-white/10 pl-4">
      <div className="label-mono text-[#ff5500]">{label}</div>
      <p className="mt-2 text-sm text-white/70">{text}</p>
    </div>
  );
}

function DeviceLabel({ no, name, desc, color }) {
  return (
    <div className="flex items-start gap-4 border-l-2 border-white/10 pl-4">
      <span
        className="mt-1 inline-block h-2 w-2 shrink-0 rounded-full"
        style={{ background: color, boxShadow: `0 0 12px ${color}` }}
      />
      <div>
        <div className="flex items-baseline gap-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
            {no}
          </span>
          <span className="font-display text-xl font-medium tracking-tight">{name}</span>
        </div>
        <p className="mt-1 text-sm text-white/55">{desc}</p>
      </div>
    </div>
  );
}

function StepDetailed({ n, t, desc }) {
  return (
    <div className="glass h-full px-5 py-5">
      <div className="label-mono">{n}</div>
      <div className="mt-2 font-display text-base font-medium tracking-tight">{t}</div>
      <p className="mt-2 text-xs text-white/55 leading-relaxed">{desc}</p>
    </div>
  );
}

function VisionCard({ badge, text, color }) {
  return (
    <div className="glass relative overflow-hidden p-6">
      <span
        className="absolute right-4 top-4 inline-block h-2 w-2 rounded-full"
        style={{ background: color, boxShadow: `0 0 14px ${color}` }}
      />
      <div className="label-mono" style={{ color }}>
        {badge}
      </div>
      <p className="mt-4 text-sm text-white/75">{text}</p>
    </div>
  );
}
